import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { authFetch } from "../api";
import Header from "../components/Header";
import ListingFilterPanel from "../components/filters/ListingFilterPanel";
import useListingFilters from "../components/filters/useListingFilters";
import { toParams } from "../components/filters/filterSpec";
import { labelStyle, selectStyle, toggleButton } from "../components/filters/styles";
import NeighborhoodSelect from "../components/NeighborhoodSelect";
import OwnershipSelect from "../components/OwnershipSelect";

/**
 * ONE INVESTOR'S BOARD — the pipeline's shape, keyed to a single investor.
 *
 * Opened in its own tab from the investors list, because an investor's board is a place you
 * WORK, not a panel you glance at: the list page used to render this underneath itself,
 * which meant losing it every time you looked at somebody else.
 *
 * The columns are deliberately the pipeline's vocabulary (Karel, 2026-10-29) so the two
 * boards read alike — but these statuses are investor_matches.status, which is PER INVESTOR.
 * Moving a listing to MIS here says it is on this client's list; it does not touch
 * realplus_listings.internal_status, which is global and means the data-quality pipeline.
 * Two boards, two meanings, one vocabulary — hence "MIS for <client>" in the header rather
 * than a bare "MIS".
 *
 * Matches are computed live on every load (the market moves daily); only the decisions are
 * stored. See api/investors.get_investor_matches.
 */

const PAGE_SIZE = 25;

/** The sale pipeline's sort list, verbatim, so the two boards offer the same choices in the
 *  same words. The combined token is the pipeline's shape; this endpoint takes the field and
 *  the direction apart, so SORT_FIELD splits it on the way out rather than giving the API a
 *  second vocabulary to understand. */
const SORT_OPTIONS = [
  { value: "date_desc",  label: "Newest First" },
  { value: "date_asc",   label: "Oldest First" },
  { value: "cap_desc",   label: "Cap Rate (High to Low)" },
  { value: "cap_asc",    label: "Cap Rate (Low to High)" },
  { value: "price_asc",  label: "Price (Low to High)" },
  { value: "price_desc", label: "Price (High to Low)" },
  { value: "dom_asc",    label: "DOM (Low to High)" },
  { value: "dom_desc",   label: "DOM (High to Low)" },
];

const SORT_FIELD = { date: "listed", cap: "cap_rate", price: "price", dom: "dom" };

/** "cap_desc" -> { sort_by: "cap_rate", sort_dir: "desc" } */
function splitSort(token) {
  const i = (token || "").lastIndexOf("_");
  const field = SORT_FIELD[token.slice(0, i)] || "listed";
  const dir = token.slice(i + 1) === "asc" ? "asc" : "desc";
  return { sort_by: field, sort_dir: dir };
}

/** Board order. Keys are what the API stores; colours mirror SaleReviewsPage column for
 *  column, so the two boards are recognisably the same object. */
const COLUMNS = [
  { key: "new",       label: "New",       color: "#e3f2fd" },
  { key: "in_review", label: "In Review", color: "#fff3e0" },
  { key: "mis",       label: "MIS",       color: "#fce4ec" },
  { key: "sent",      label: "Sent",      color: "#e8f5e9" },
  { key: "rejected",  label: "Rejected",  color: "#f5f5f5" },
];

/** The preposition is per status: you send something TO someone, but a listing is new,
 *  in review, MIS or rejected FOR them. */
const PREP = { sent: "to" };
const forClient = (col, who) => (who ? `${col.label} ${PREP[col.key] || "for"} ${who}` : col.label);

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString()}`);
const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return isNaN(d) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

/** The GLOBAL data-quality state, in the pipeline's own words — distinct from the column a
 *  card sits in, which is this investor's decision. */
const PIPELINE_LABEL = {
  TO_REVIEW: "New", VERIFIED: "New", IN_REVIEW: "In Review", REVIEWED: "In Review",
  MIS: "MIS", RETIRED: "Retired", TOWNHOUSE: "Townhouse",
};

export default function InvestorBoardPage() {
  const { id } = useParams();
  const [investor, setInvestor] = useState(null);
  const [cols, setCols] = useState({});        // key -> {listings, total, page}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dragged, setDragged] = useState(null);
  const [saving, setSaving] = useState(false);
  const [criteria, setCriteria] = useState({ label: null, unapplied: [], impossible: [] });
  const [sortBy, setSortBy] = useState("date_desc");

  // ── email mode ──────────────────────────────────────────────────────────────────────
  // A MODE rather than an always-on checkbox per card: the board's primary gesture is drag,
  // and a permanent checkbox in the corner of every card competes with it both visually and
  // for the mouse. Off by default, so the board stays a board.
  const [emailMode, setEmailMode] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const [composing, setComposing] = useState(false);
  const [sendState, setSendState] = useState(null);   // null | "sending" | {sent, failed}

  const filters = useListingFilters();

  // Neighbourhood and ownership are NOT in the shared filter spec -- on the pipeline they
  // are page-level controls too. An investor's search is mostly WHERE and WHAT, so the board
  // would be showing the least important half of it without them.
  const [hoods, setHoods] = useState([]);              // the options
  const [selectedHoods, setSelectedHoods] = useState([]);
  const [ownership, setOwnership] = useState([]);
  // Until the panel has been filled in from the criteria, the board must NOT claim its
  // filters are the whole search -- see exact_filters in api/investors.
  const [seeded, setSeeded] = useState(false);
  const appliedFilters = filters.applied;   // never depend on `filters` itself — it is a new
                                            // object every render and would spin the effect
  const pages = useRef({});

  const loadColumn = useCallback(async (key, page) => {
    const { sort_by, sort_dir } = splitSort(sortBy);
    const qs = new URLSearchParams({ status: key, page: String(page), limit: String(PAGE_SIZE),
                                     sort_by, sort_dir });
    toParams(appliedFilters, qs);
    if (selectedHoods.length) qs.set("neighborhood_ids", selectedHoods.join(","));
    if (ownership.length) qs.set("ownership_type", ownership.join(","));
    // Only once the panel actually holds the criteria. Before that the server should keep
    // applying them itself, or the first paint would be the whole city.
    if (seeded) qs.set("exact_filters", "1");
    const r = await authFetch(`/investors/${id}/matches?${qs.toString()}`);
    if (!r || r.detail) throw new Error(r?.detail || "could not load matches");
    return r;
  }, [id, appliedFilters, sortBy, selectedHoods, ownership, seeded]);

  const loadAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const results = await Promise.all(
        COLUMNS.map((c) => loadColumn(c.key, pages.current[c.key] || 1)));
      const next = {};
      COLUMNS.forEach((c, i) => {
        next[c.key] = { listings: results[i].listings || [], total: results[i].total || 0 };
      });
      setCols(next);
      const first = results[0] || {};
      setCriteria({
        label: first.criteria_label,
        unapplied: first.unapplied || [],
        impossible: first.impossible || [],
      });
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [loadColumn]);

  useEffect(() => {
    authFetch(`/investors/${id}`).then((r) => setInvestor(r && !r.detail ? r : null)).catch(() => {});
    authFetch("/sale-reviews/neighborhoods")
      .then((r) => setHoods(r?.neighborhoods || [])).catch(() => {});
  }, [id]);

  // Seed the controls from the investor's saved search, ONCE, before the first board load.
  // Karel: "so user will know what we're filtering on" -- previously the board showed the
  // results of a search without showing the search.
  const seedFromCriteria = useCallback(async () => {
    try {
      const r = await authFetch(`/investors/${id}/criteria-filters`);
      if (!r || r.detail) { setSeeded(true); return; }
      const f = r.filters || {};
      setSelectedHoods(f.neighborhood_ids || []);
      setOwnership(typeof f.ownership_type === "string"
        ? f.ownership_type.split(",").filter(Boolean) : []);
      // load() sets the controls AND the applied set together; the two page-level values
      // above are sent alongside rather than through it.
      const { neighborhood_ids, ownership_type, ...panel } = f;
      filters.load(panel);
    } finally {
      setSeeded(true);
    }
    // filters is a fresh object every render -- depending on it would spin this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { seedFromCriteria(); }, [seedFromCriteria]);

  // Pre-filled from the investor's own record, then editable: the analyst should SEE who is
  // about to be written to rather than trust that the right addresses are on file.
  const [toField, setToField] = useState("");
  useEffect(() => {
    if (composing) setToField((investor?.emails || []).join(", "));
  }, [composing, investor]);
  const [message, setMessage] = useState("");
  const [withPhotos, setWithPhotos] = useState(true);

  const send = async () => {
    const emails = toField.split(",").map((e) => e.trim()).filter(Boolean);
    if (!emails.length) { alert("Add at least one recipient."); return; }
    setSendState("sending");
    try {
      const res = await authFetch(`/investors/${id}/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          listing_ids: [...picked],
          emails,
          custom_message: message,
          show_photos: withPhotos,
        }),
      });
      if (res?.detail) { setSendState(null); alert(res.detail); return; }
      setSendState(res);
    } catch (e) {
      setSendState(null);
      alert(`Could not send: ${e.message || e}`);
    }
  };

  useEffect(() => { loadAll(); }, [loadAll]);

  const who = investor?.household_name || (investor ? `Investor #${investor.id}` : "");

  // ── drag & drop ──────────────────────────────────────────────────────────────────────
  const onDragStart = (e, l, from) => { setDragged({ ...l, _from: from }); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  const onDrop = async (e, to) => {
    e.preventDefault();
    const card = dragged;
    setDragged(null);
    if (!card || card._from === to) return;

    // Optimistic: the card moves under the cursor, then the server confirms. A refusal puts
    // it back and says why, rather than letting a silent reload snap it home unexplained.
    setCols((prev) => {
      const next = { ...prev };
      next[card._from] = {
        listings: (prev[card._from]?.listings || []).filter((x) => x.id !== card.id),
        total: Math.max(0, (prev[card._from]?.total || 1) - 1),
      };
      next[to] = {
        listings: [{ ...card, investor_status: to }, ...(prev[to]?.listings || [])],
        total: (prev[to]?.total || 0) + 1,
      };
      return next;
    });

    setSaving(true);
    try {
      const res = await authFetch(`/investors/${id}/matches/${card.id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: to }),
      });
      if (res?.detail) { alert(`Could not move this listing: ${res.detail}`); await loadAll(); }
    } catch (err) {
      alert(`Could not move this listing: ${err.message || err}`);
      await loadAll();
    } finally {
      setSaving(false);
    }
  };

  const turnPage = async (key, delta) => {
    const cur = pages.current[key] || 1;
    const total = cols[key]?.total || 0;
    const last = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const next = Math.min(last, Math.max(1, cur + delta));
    if (next === cur) return;
    pages.current[key] = next;
    const r = await loadColumn(key, next);
    setCols((prev) => ({ ...prev, [key]: { listings: r.listings || [], total: r.total || 0 } }));
  };

  const togglePick = (listingId) => setPicked((prev) => {
    const next = new Set(prev);
    next.has(listingId) ? next.delete(listingId) : next.add(listingId);
    return next;
  });

  /** Leaving email mode drops the selection. Keeping it would mean a hidden set of cards
   *  staged for sending with nothing on screen saying so. */
  const leaveEmailMode = () => { setEmailMode(false); setPicked(new Set()); setComposing(false); };

  const applyFilters = () => { pages.current = {}; filters.apply(); };
  // Reset means "back to THEIR search", not "no filters at all". With exact_filters the
  // board's controls are the whole truth, so clearing them would ask for every active sale
  // in the city -- which is never what Reset means on an investor's board.
  const resetFilters = () => { pages.current = {}; seedFromCriteria(); };

  // ── styles, mirrored from SaleReviewsPage so the boards look like one product ─────────
  const page = { minHeight: "100vh", background: "#f5f6f8" };
  // Verbatim from SaleReviewsPage's containerStyle. The board was full-bleed while the
  // pipeline is centred at 1800px, so the two did not line up when you moved between tabs.
  const wrap = { padding: "20px", maxWidth: "1800px", margin: "0 auto" };
  const board = { display: "grid", gridTemplateColumns: `repeat(${COLUMNS.length}, 1fr)`, gap: "20px", marginTop: "20px" };
  const column = { background: "#fff", border: "1px solid #e3e7ec", borderRadius: "10px", padding: "10px", display: "flex", flexDirection: "column", minHeight: "60vh" };
  const colHeader = (bg) => ({ display: "flex", justifyContent: "space-between", alignItems: "center", background: bg, borderRadius: "8px", padding: "8px 10px", fontWeight: 700, fontSize: "13px", color: "#2b323b", marginBottom: "10px" });
  const badge = { background: "rgba(0,0,0,0.08)", borderRadius: "10px", padding: "1px 8px", fontSize: "12px", fontWeight: 700 };
  const card = { background: "#fff", border: "1px solid #e3e7ec", borderRadius: "8px", padding: "10px 11px", marginBottom: "9px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", cursor: "grab", transition: "transform .08s, box-shadow .08s" };
  const addr = { fontWeight: 700, fontSize: "13px", color: "#111", marginBottom: "4px" };
  const line = { fontSize: "12px", color: "#5a626c", display: "flex", justifyContent: "space-between", gap: "8px" };

  if (!loading && error) {
    return (
      <div style={page}><Header showLogout={true} />
        <div style={wrap}><div style={{ ...column, minHeight: 0, color: "#b3261e" }}>{error}</div></div>
      </div>
    );
  }

  return (
    <div style={page}>
      <Header showLogout={true} />
      <div style={wrap}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "10px", flexWrap: "wrap" }}>
          <h1 style={{ fontSize: "22px", fontWeight: 700, color: "#111", margin: 0 }}>
            {who || "Investor"}
          </h1>
          {criteria.label && (
            <span style={{ fontSize: "13px", color: "#6b7580" }}>· {criteria.label}</span>
          )}
          <a href={`/investors/${id}/edit`} style={{ fontSize: "12px", color: "#1f6feb" }}>Edit search</a>
          {saving && <span style={{ fontSize: "12px", color: "#6b7580" }}>saving…</span>}

        </div>

        {/* An impossible search and a merely narrow one both render as five empty columns.
            Say which this is, or the only way to find out is to ask someone to go reading
            SQL -- which is exactly how Michal & Yosi's inverted price band surfaced. */}
        {criteria.impossible.length > 0 && (
          <div style={{ marginTop: 14, padding: "11px 14px", borderRadius: 8,
                        background: "#fff4e5", border: "1px solid #ffcc80", color: "#8a4b00",
                        fontSize: 13.5 }}>
            <strong>This search can never match.</strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {criteria.impossible.map((p) => (
                <li key={p.field} style={{ marginBottom: 2 }}>
                  <strong>{p.field}</strong> — {p.reason}
                </li>
              ))}
            </ul>
            <div style={{ marginTop: 6 }}>
              <a href={`/investors/${id}/edit`} style={{ color: "#8a4b00", fontWeight: 600 }}>
                Edit this investor's search →
              </a>
            </div>
          </div>
        )}

        <ListingFilterPanel
          scope="listings"
          values={filters.values}
          set={filters.set}
          onSearch={applyFilters}
          onReset={resetFilters}
          loading={loading}
          leading={
            <>
              <NeighborhoodSelect
                neighborhoods={hoods}
                selected={selectedHoods}
                onChange={setSelectedHoods}
                placeholder="All neighborhoods"
                gridColumn="span 2"
              />
              <OwnershipSelect selected={ownership} onChange={setOwnership} />
            </>
          }
          // Beside "More filters", built from the same toggleButton the row already uses,
          // so the heights agree by construction rather than by two sets of numbers that
          // have to be kept level by hand.
          actions={!emailMode ? (
            <button type="button" onClick={() => setEmailMode(true)} style={toggleButton}>
              ✉ Email listings
            </button>
          ) : (
            <>
              <button type="button" disabled={picked.size === 0}
                      onClick={() => setComposing(true)}
                      style={{ ...toggleButton, fontWeight: 700, border: "none", color: "#fff",
                               background: picked.size ? "#1f6feb" : "#c8cfd8",
                               cursor: picked.size ? "pointer" : "default" }}>
                {picked.size ? `Send ${picked.size}` : "Select listings…"}
              </button>
              <button type="button" onClick={leaveEmailMode}
                      style={{ ...toggleButton, color: "#6b7580" }}>
                Cancel
              </button>
            </>
          )}
          // `trailing` renders after the filters, which in the collapsed row means directly
          // beside Days listed -- exactly where the pipeline keeps its Sort By. Same slot,
          // same styles, so the control sits in the same place on both boards.
          trailing={
            <div>
              <label style={labelStyle}>Sort By</label>
              <select value={sortBy}
                      onChange={(e) => { pages.current = {}; setSortBy(e.target.value); }}
                      style={selectStyle}>
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          }
        />

        {composing && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", zIndex: 50,
                        display: "flex", alignItems: "center", justifyContent: "center" }}
               onClick={() => { if (sendState !== "sending") { setComposing(false); setSendState(null); } }}>
            <div onClick={(e) => e.stopPropagation()}
                 style={{ background: "#fff", borderRadius: 10, padding: 22, width: 560,
                          maxWidth: "92%", boxShadow: "0 8px 30px rgba(0,0,0,.25)" }}>
              {sendState && sendState !== "sending" ? (
                <>
                  <h2 style={{ margin: "0 0 10px", fontSize: 18 }}>
                    {sendState.failed?.length ? "Sent, with problems" : "Sent"}
                  </h2>
                  <p style={{ fontSize: 13.5, color: "#2b323b", margin: "0 0 6px" }}>
                    {sendState.listing_count} listing{sendState.listing_count === 1 ? "" : "s"}
                    {sendState.show_photos ? " with photos" : " as a list"} to{" "}
                    {sendState.sent?.join(", ") || "nobody"}.
                  </p>
                  {sendState.moved_to_sent > 0 && (
                    <p style={{ fontSize: 13, color: "#6b7580", margin: "0 0 6px" }}>
                      Moved {sendState.moved_to_sent} to <strong>Sent</strong> on this board.
                    </p>
                  )}
                  {sendState.failed?.length > 0 && (
                    <p style={{ fontSize: 13, color: "#b3261e", margin: "0 0 6px" }}>
                      Failed: {sendState.failed.join(", ")}
                    </p>
                  )}
                  {/* Reload on the way out: the server has just moved these listings to Sent,
                      and a board still showing them under New would be lying about state the
                      user can see has changed. */}
                  <button type="button"
                          onClick={() => { setSendState(null); leaveEmailMode(); pages.current = {}; loadAll(); }}
                          style={{ marginTop: 10, padding: "7px 14px", fontWeight: 600,
                                   border: "none", borderRadius: 6, background: "#1f6feb",
                                   color: "#fff", cursor: "pointer" }}>
                    Done
                  </button>
                </>
              ) : (
                <>
                  <h2 style={{ margin: "0 0 4px", fontSize: 18 }}>
                    Email {picked.size} listing{picked.size === 1 ? "" : "s"}
                  </h2>
                  <p style={{ margin: "0 0 14px", fontSize: 13, color: "#6b7580" }}>
                    to {who || "this investor"}
                  </p>

                  <label style={{ ...labelStyle, display: "block" }}>To</label>
                  <input value={toField} onChange={(e) => setToField(e.target.value)}
                         placeholder="name@example.com, another@example.com"
                         style={{ width: "100%", padding: "8px 10px", fontSize: 13,
                                  border: "1px solid #d7dce3", borderRadius: 6, marginBottom: 12,
                                  boxSizing: "border-box" }} />

                  <label style={{ ...labelStyle, display: "block" }}>Message</label>
                  <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                            rows={5} placeholder="A note to go above the listings…"
                            style={{ width: "100%", padding: "8px 10px", fontSize: 13,
                                     border: "1px solid #d7dce3", borderRadius: 6,
                                     marginBottom: 12, boxSizing: "border-box",
                                     fontFamily: "inherit", resize: "vertical" }} />

                  <label style={{ ...labelStyle, display: "block" }}>Format</label>
                  <div style={{ display: "flex", gap: 16, marginBottom: 16, fontSize: 13 }}>
                    <label style={{ cursor: "pointer" }}>
                      <input type="radio" checked={withPhotos} onChange={() => setWithPhotos(true)} />
                      {" "}Photos — a card per listing
                    </label>
                    <label style={{ cursor: "pointer" }}>
                      <input type="radio" checked={!withPhotos} onChange={() => setWithPhotos(false)} />
                      {" "}List only — no images
                    </label>
                  </div>

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <button type="button" onClick={() => setComposing(false)}
                            disabled={sendState === "sending"}
                            style={{ padding: "8px 14px", fontSize: 13, borderRadius: 6,
                                     border: "1px solid #d7dce3", background: "#fff",
                                     cursor: "pointer" }}>
                      Cancel
                    </button>
                    <button type="button" onClick={send} disabled={sendState === "sending"}
                            style={{ padding: "8px 16px", fontSize: 13, fontWeight: 700,
                                     borderRadius: 6, border: "none", color: "#fff",
                                     background: sendState === "sending" ? "#9bb8e8" : "#1f6feb",
                                     cursor: sendState === "sending" ? "default" : "pointer" }}>
                      {sendState === "sending" ? "Sending…" : "Send"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ padding: "40px 0", color: "#6b7580" }}>Loading…</div>
        ) : (
          <div style={board}>
            {COLUMNS.map((col) => {
              const data = cols[col.key] || { listings: [], total: 0 };
              const cur = pages.current[col.key] || 1;
              const last = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
              return (
                <div key={col.key} style={column}
                     onDragOver={onDragOver} onDrop={(e) => onDrop(e, col.key)}>
                  <div style={colHeader(col.color)}>
                    <span title={forClient(col, who)}>{col.label}</span>
                    <span style={badge}>{data.total.toLocaleString()}</span>
                  </div>

                  <div style={{ flex: 1 }}>
                    {data.listings.map((l) => (
                      <div key={l.id}
                           style={{ ...card,
                                    cursor: emailMode ? "pointer" : "grab",
                                    borderColor: picked.has(l.id) ? "#1f6feb" : "#e3e7ec",
                                    background: picked.has(l.id) ? "#f2f7ff" : "#fff" }}
                           // Dragging is OFF while picking: the two gestures start the same
                           // way, and a half-drag that turns out to be a click is how you
                           // move a card you only meant to tick.
                           draggable={!emailMode}
                           onClick={emailMode ? () => togglePick(l.id) : undefined}
                           onDragStart={(e) => onDragStart(e, l, col.key)}
                           onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 3px 8px rgba(0,0,0,0.14)"; }}
                           onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)"; }}>
                        <div style={addr}>
                          {emailMode && (
                            <input type="checkbox" readOnly checked={picked.has(l.id)}
                                   style={{ marginRight: 7, verticalAlign: "middle" }} />
                          )}
                          <a href={`/predict/${l.id}`} target="_blank" rel="noreferrer"
                             onClick={(e) => e.stopPropagation()}
                             style={{ color: "inherit", textDecoration: "none" }}>
                            {l.address}{l.unit_number ? ` #${l.unit_number}` : ""}
                          </a>
                        </div>
                        <div style={line}>
                          <span>{l.neighborhood || "—"}</span>
                          <span>{l.bedrooms ?? "—"}bd / {l.bathrooms ?? "—"}ba</span>
                        </div>
                        <div style={line}>
                          <span>{money(l.list_price)}</span>
                          <span>{l.cap_rate == null ? "—" : `${l.cap_rate}% cap`}</span>
                        </div>
                        <div style={line}>
                          <span>{l.predicted_rent == null ? "no AI rent" : `${money(l.predicted_rent)}/mo`}</span>
                          <span>{fmtDate(l.listed_at)}</span>
                        </div>
                        {l.pipeline_status && (
                          <div style={{ marginTop: "5px" }}>
                            <span title="The global data-quality state, shared by everyone — not this investor's"
                                  style={{ fontSize: "10.5px", fontWeight: 700, color: "#5a626c",
                                           background: "#eef1f4", borderRadius: "4px", padding: "1px 6px" }}>
                              {PIPELINE_LABEL[l.pipeline_status] || l.pipeline_status}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                    {data.listings.length === 0 && (
                      <div style={{ fontSize: "12px", color: "#9aa3ad", padding: "10px 2px" }}>
                        Nothing here yet — drag a listing in.
                      </div>
                    )}
                  </div>

                  {last > 1 && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12px", color: "#6b7580", paddingTop: "8px" }}>
                      <button onClick={() => turnPage(col.key, -1)} disabled={cur <= 1}
                              style={{ cursor: cur <= 1 ? "default" : "pointer" }}>‹</button>
                      <span>{cur} / {last}</span>
                      <button onClick={() => turnPage(col.key, 1)} disabled={cur >= last}
                              style={{ cursor: cur >= last ? "default" : "pointer" }}>›</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
